import React, { useMemo, useCallback } from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { B200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { InfoFlag } from '@atlassian/jira-flags';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type AnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { CUSTOM_NAV_BAR_COLORS_FLAG_ID, HELP_ARTICLE_ID } from '../common/constants';
import messages from './messages';

export const CUSTOM_NAV_BAR_COLORS_FLAG_TEST_ID =
	'router-resources.theme-preference.custom-nav-bar-colors-flag';

type Props = {
	onDismiss: () => void;
	analyticsAttributes: {
		backgroundColor: string;
		highlightColor: string;
	};
};

const CustomNavBarColorsFlag = ({ onDismiss, analyticsAttributes }: Props) => {
	const { formatMessage } = useIntl();
	const onOpenInProductHelpArticle = useHelpPanelLink();

	const onDismissFn = useCallback(
		(
			_: React.MouseEvent<HTMLElement, MouseEvent> | string | number,
			analyticsEvent: AnalyticsEvent,
		) => {
			fireUIAnalytics(
				analyticsEvent,
				'customTopNavUserFlagWarningDismissButton',
				analyticsAttributes,
			);
			onDismiss();
		},
		[onDismiss, analyticsAttributes],
	);

	return useMemo(
		() => (
			<InfoFlag
				icon={<InfoIcon primaryColor={token('color.icon.information', B200)} label="" />}
				testId={CUSTOM_NAV_BAR_COLORS_FLAG_TEST_ID}
				id={CUSTOM_NAV_BAR_COLORS_FLAG_ID}
				title={formatMessage(messages.title)}
				description={formatMessage(messages.description)}
				onDismissed={onDismissFn}
				actions={[
					{
						content: formatMessage(messages.learnMoreAction),
						onClick: (_, analyticsEvent: AnalyticsEvent) => {
							fireUIAnalytics(
								analyticsEvent,
								'customTopNavUserFlagWarningMoreButton',
								analyticsAttributes,
							);
							onOpenInProductHelpArticle?.(HELP_ARTICLE_ID);
						},
					},
					{
						content: formatMessage(messages.dismissAction),
						onClick: onDismissFn,
					},
				]}
			/>
		),
		[formatMessage, onDismissFn, onOpenInProductHelpArticle, analyticsAttributes],
	);
};

export default CustomNavBarColorsFlag;
